import { useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useRevalidator } from 'react-router';
import type { RealtimeClient } from './client';
import { RealtimeContext } from './context';
import { realtimeClient } from './realtime-client';

export interface RealtimeProviderProps {
  readonly children: ReactNode;
  /** Tests inject a fake. The app leaves it out and gets the singleton. */
  readonly client?: RealtimeClient;
}

/**
 * Connects reconnection to the data layer (P0-WEB-05).
 *
 * That is the whole job, and it is the one thing no socket library can do for
 * you. pusher-js reconnects and resubscribes on its own; what it cannot know is
 * that the events it missed are gone for good. From the contract's client
 * rules:
 *
 *   "Realtime is an accelerator, never the source of truth. Reconnect must
 *    re-fetch run state over HTTP, because events emitted while the socket was
 *    down are not replayed."
 *
 * Route loaders are the portal's data layer, so `revalidate()` re-runs every
 * active loader — which is exactly that re-fetch. Without it, a user who shut
 * their laptop for ten minutes comes back to a progress bar frozen where the
 * socket dropped, with nothing saying it is stale.
 *
 * Must be rendered inside the router, because `useRevalidator` needs it. That
 * is why it lives in `AppLayout` rather than in the provider stack in `App.tsx`.
 */
export function RealtimeProvider({ children, client }: RealtimeProviderProps) {
  const { revalidate } = useRevalidator();

  // A client supplied above this point wins. Since this provider has to live
  // inside the route tree, a test cannot wrap the router in one — deferring to
  // the context lets a test supply a fake through `<RealtimeContext value={…}>`
  // from outside, instead of every render of the shell opening a real socket.
  const inherited = useContext(RealtimeContext);
  const supplied = client ?? inherited;

  const resolved = useMemo(() => supplied ?? realtimeClient(), [supplied]);

  useEffect(() => {
    // Bound in an effect rather than at construction because `revalidate` is
    // the router's, and the client outlives any one router instance. Reconnects
    // only happen after mount, so there is no window where it is unset.
    resolved.setOnReconnect(() => {
      void revalidate();
    });

    return () => {
      // Cleared rather than left pointing at an unmounted tree's revalidator.
      resolved.setOnReconnect(undefined);
    };
  }, [resolved, revalidate]);

  return <RealtimeContext value={resolved}>{children}</RealtimeContext>;
}
